import * as _ from 'lodash';
import {
  AggregationFunctionType,
  generateCondition,
  generateFunction,
  generatePagination,
  generateSorts,
  generateSubquery,
  HelisaConditionalField,
  HelisaFunctionField,
  HelisaOperation,
  HelisaQueryAggregation,
  HelisaQueryCondition,
  HelisaQueryFilter,
  HelisaQueryFunction,
  HelisaQueryGroup,
  HelisaQuerySort,
  HelisaSubqueryField,
  isHelisaFunctionField,
  isHelisaSubqueryField,
} from './query-definitions';

export class HelisaQuery {
  private filter: HelisaQueryFilter | null;
  private sorts: Array<HelisaQuerySort>; // check
  private groups: Array<string | HelisaQueryGroup>; // check
  private having: HelisaQueryFilter | null;
  private aggregations: Array<HelisaQueryAggregation>; // check
  private fields: Array<
    string | HelisaConditionalField | HelisaFunctionField | HelisaSubqueryField
  >; // check
  private unwindFields: Array<string>;
  private subGroups: Array<string | HelisaQueryGroup>; // check
  private subAggregations: Array<HelisaQueryAggregation>; // check
  private groupFilter: HelisaQueryFilter | null;

  constructor() {
    this.sorts = [];
    this.fields = [];
    this.groups = [];
    this.having = null;
    this.aggregations = [];
    this.filter = null;
    this.unwindFields = [];
    this.subGroups = [];
    this.subAggregations = [];
    this.groupFilter = null;
  }

  addSort(fieldName: string, order: 'asc' | 'desc' = 'asc'): HelisaQuery {
    this.sorts.push({ fieldName, order });
    return this;
  }

  removeSort(fieldName: string): HelisaQuery {
    const index: number = this.sorts.findIndex(
      (item: HelisaQuerySort): boolean => item.fieldName === fieldName
    );
    if (index >= 0) {
      this.sorts.splice(index, 1);
    }
    return this;
  }

  loadFields(source: any, keyObj: string | null = null): HelisaQuery {
    const keys: string[] = Object.keys(source);
    keys.forEach((key: string): void => {
      const item: string = source[key];
      if (_.isObject(item) && !_.isNull(item)) {
        if (!_.isArray(item)) {
          this.loadFields(item, key);
        } else if (_.isArray(item)) {
          if ((item as Array<string>).length > 0) {
            this.loadFields(item[0], key);
          } else {
            this.addField(key);
          }
        }
      } else {
        if (keyObj !== null) {
          this.addField(keyObj + '.' + key);
        } else {
          this.addField(key);
        }
      }
    });
    return this;
  }

  addField(
    fieldName:
      | string
      | HelisaConditionalField
      | HelisaFunctionField
      | HelisaSubqueryField
  ): HelisaQuery {
    this.fields.push(fieldName);
    return this;
  }

  addFields(
    ...fieldNames: Array<
      | string
      | HelisaConditionalField
      | HelisaFunctionField
      | HelisaSubqueryField
    >
  ): HelisaQuery {
    this.fields.push(...fieldNames);
    return this;
  }

  removeField(
    fieldName:
      | string
      | HelisaConditionalField
      | HelisaFunctionField
      | HelisaSubqueryField
  ): HelisaQuery {
    const index: number = this.fields.indexOf(fieldName);
    if (index >= 0) {
      this.fields.splice(index, 1);
    }
    return this;
  }

  addFieldGroup(...fieldNames: Array<string | HelisaQueryGroup>): HelisaQuery {
    this.groups.push(...fieldNames);
    return this;
  }

  removeFieldGroup(fieldName: string | HelisaQueryGroup): HelisaQuery {
    const index: number = this.groups.indexOf(fieldName);
    if (index >= 0) {
      this.groups.splice(index, 1);
    }
    return this;
  }

  addFieldSubGroup(
    ...fieldNames: Array<string | HelisaQueryGroup>
  ): HelisaQuery {
    this.subGroups.push(...fieldNames);
    return this;
  }

  removeFieldSubGroup(fieldName: string | HelisaQueryGroup): HelisaQuery {
    const index: number = this.subGroups.indexOf(fieldName);
    if (index >= 0) {
      this.subGroups.splice(index, 1);
    }
    return this;
  }

  addAggregation(
    fieldResponseName: string,
    type:
      | AggregationFunctionType
      | 'array_agg'
      | 'json_agg'
      | 'json_object_agg'
      | 'string_agg',
    fieldName?: string
  ): HelisaQuery {
    if (type !== 'count' && !fieldName) {
      throw new Error('Debe enviar el field name para la suma y el promedio');
    }
    this.aggregations.push({
      fieldResponseName,
      fieldName,
      helisaQueryCondition: null,
      type,
      operation: null,
      sort: null,
      delimiter: null,
      functionField: null,
    });
    return this;
  }

  addAggregationConditionalField(
    fieldResponseName: string,
    type:
      | AggregationFunctionType
      | 'array_agg'
      | 'json_agg'
      | 'json_object_agg'
      | 'string_agg',
    helisaQueryCondition: HelisaQueryCondition
  ): HelisaQuery {
    this.aggregations.push({
      fieldResponseName,
      fieldName: null,
      helisaQueryCondition,
      type,
      operation: null,
      sort: null,
      delimiter: null,
      functionField: null,
    });
    return this;
  }

  addAggregationWithOperation(
    fieldResponseName: string,
    type:
      | AggregationFunctionType
      | 'array_agg'
      | 'json_agg'
      | 'json_object_agg'
      | 'string_agg',
    operation: HelisaOperation
  ): HelisaQuery {
    this.aggregations.push({
      fieldResponseName,
      fieldName: null,
      helisaQueryCondition: null,
      type,
      operation,
      sort: null,
      delimiter: null,
      functionField: null,
    });
    return this;
  }

  addAggregationWithFunctionField(
    fieldResponseName: string,
    fieldName: string,
    type:
      | AggregationFunctionType
      | 'array_agg'
      | 'json_agg'
      | 'json_object_agg'
      | 'string_agg',
    operation: HelisaOperation,
    sort: Array<HelisaQuerySort>,
    delimiter: string,
    functionField: HelisaQueryFunction
  ): HelisaQuery {
    this.aggregations.push({
      fieldResponseName,
      fieldName,
      helisaQueryCondition: null,
      type,
      operation,
      sort,
      delimiter,
      functionField,
    });
    return this;
  }

  // tslint:disable-next-line:no-shadowed-variable
  addAggregationWithFunctionFieldOperation(
    fieldResponseName: string,
    type:
      | AggregationFunctionType
      | 'array_agg'
      | 'json_agg'
      | 'json_object_agg'
      | 'string_agg',
    operation: HelisaOperation,
    sort: Array<HelisaQuerySort>,
    delimiter: string,
    functionField: HelisaQueryFunction
  ): HelisaQuery {
    this.aggregations.push({
      fieldResponseName,
      fieldName: null,
      helisaQueryCondition: null,
      type,
      operation,
      sort,
      delimiter,
      functionField,
    });
    return this;
  }

  // tslint:disable-next-line:no-shadowed-variable
  addAggregationWithFunctionFieldCondition(
    fieldResponseName: string,
    type:
      | AggregationFunctionType
      | 'array_agg'
      | 'json_agg'
      | 'json_object_agg'
      | 'string_agg',
    helisaQueryCondition: HelisaQueryCondition,
    sort: Array<HelisaQuerySort>,
    delimiter: string,
    functionField: HelisaQueryFunction
  ): HelisaQuery {
    this.aggregations.push({
      fieldResponseName,
      fieldName: null,
      helisaQueryCondition,
      type,
      operation: null,
      sort,
      delimiter,
      functionField,
    });
    return this;
  }

  // tslint:disable-next-line:no-shadowed-variable
  addAggregationSingleFunctionField(
    fieldResponseName: string,
    fieldName: string,
    type:
      | AggregationFunctionType
      | 'array_agg'
      | 'json_agg'
      | 'json_object_agg'
      | 'string_agg',
    sort: Array<HelisaQuerySort>,
    delimiter: string,
    functionField: HelisaQueryFunction
  ): HelisaQuery {
    this.aggregations.push({
      fieldResponseName,
      fieldName,
      helisaQueryCondition: null,
      type,
      operation: null,
      sort,
      delimiter,
      functionField,
    });
    return this;
  }

  // tslint:disable-next-line:no-shadowed-variable
  addAggregationStringAgg(
    fieldResponseName: string,
    fieldName: string,
    type:
      | AggregationFunctionType
      | 'array_agg'
      | 'json_agg'
      | 'json_object_agg'
      | 'string_agg',
    sort: Array<HelisaQuerySort>,
    delimiter: string
  ): HelisaQuery {
    this.aggregations.push({
      fieldResponseName,
      fieldName,
      helisaQueryCondition: null,
      type,
      operation: null,
      sort,
      delimiter,
      functionField: null,
    });
    return this;
  }

  addSubAggregation(
    fieldResponseName: string,
    type:
      | AggregationFunctionType
      | 'array_agg'
      | 'json_agg'
      | 'json_object_agg'
      | 'string_agg',
    fieldName?: string
  ): HelisaQuery {
    if (type !== 'count' && !fieldName) {
      throw new Error('Debe enviar el field name para la suma y el promedio');
    }
    this.subAggregations.push({
      fieldResponseName,
      fieldName,
      helisaQueryCondition: null,
      type,
      operation: null,
    });
    return this;
  }

  removeAggregation(fieldName: string): HelisaQuery {
    const index: number = this.aggregations.findIndex(
      (item: HelisaQueryAggregation): boolean => item.fieldName === fieldName
    );
    if (index >= 0) {
      this.aggregations.splice(index, 1);
    }
    return this;
  }

  removeSubAggregation(fieldName: string): HelisaQuery {
    const index: number = this.subAggregations.findIndex(
      (item: HelisaQueryAggregation): boolean => item.fieldName === fieldName
    );
    if (index >= 0) {
      this.subAggregations.splice(index, 1);
    }
    return this;
  }

  where(filter: HelisaQueryFilter): HelisaQuery {
    this.filter = filter;
    return this;
  }

  whereHaving(having: HelisaQueryFilter): HelisaQuery {
    this.having = having;
    return this;
  }

  addUnwind(...fieldNames: string[]): HelisaQuery {
    this.unwindFields = fieldNames;
    return this;
  }

  groupWhere(filter: HelisaQueryFilter): HelisaQuery {
    this.groupFilter = filter;
    return this;
  }

  build(page: number = 0, size: number = 50): string {
    // tslint:disable-next-line:no-any
    const obj: Array<any> = [];
    if (this.unwindFields.length > 0) {
      obj.push(...this.generateUnwindFields());
    }
    if (this.filter) {
      obj.push({ $match: { $expr: this.filter.generateFilter() } });
    }
    if (this.groups.length > 0) {
      obj.push(this.generateGroups(this.groups, this.aggregations));
    }
    if (this.subGroups.length > 0 && this.groups.length > 0) {
      obj.push(this.generateGroups(this.subGroups, this.subAggregations));
    }
    if (this.groupFilter) {
      obj.push({ $match: { $expr: this.groupFilter.generateFilter() } });
    }
    if (this.fields.length > 0 && this.groups.length === 0) {
      obj.push(this.generateFields());
    }
    if (this.having) {
      obj.push({ $having: { $expr: this.having.generateFilter() } });
    }
    if (this.sorts.length > 0) {
      obj.push(this.generateSorts());
    }
    /* No cambiar nunca la posición de la paginación, para mongo search es importantisisisimo */
    obj.push(...generatePagination(page, size));
    return JSON.stringify(obj);
  }

  // tslint:disable-next-line:no-any
  private generateFields(): any {
    // tslint:disable-next-line:no-any
    const project: any = {};
    this.fields.forEach(
      (
        fieldName:
          | string
          | HelisaConditionalField
          | HelisaFunctionField
          | HelisaSubqueryField
      ): void => {
        if (typeof fieldName === 'string') {
          project[fieldName] = 1;
        } else if (isHelisaFunctionField(fieldName)) {
          project[fieldName.fieldName] = generateFunction(
            fieldName.functionObject
          );
        } else if (isHelisaSubqueryField(fieldName)) {
          project[fieldName.fieldName] = generateSubquery(
            fieldName.subqueryObject
          );
        } else {
          project[fieldName.fieldName] = generateCondition(fieldName.condition);
        }
      }
    );
    return { $project: project };
  }

  // tslint:disable-next-line:no-any
  private generateSorts(): any {
    // tslint:disable-next-line:no-any
    const sort: any = {};
    this.sorts.forEach((fieldSort: HelisaQuerySort): void => {
      sort[fieldSort.fieldName] = fieldSort.order === 'asc' ? 1 : -1;
    });
    return { $sort: sort };
  }

  // tslint:disable-next-line:no-any
  private generateUnwindFields(): any[] {
    // tslint:disable-next-line:no-any
    const unwind: any[] = [];
    this.unwindFields.forEach((fieldUnwind: string): void => {
      unwind.push({
        $unwind: { path: '$' + fieldUnwind, preserveNullAndEmptyArrays: true },
      });
    });
    return unwind;
  }

  // tslint:disable-next-line:no-any
  private generateGroups(
    sourceGroup: Array<string | HelisaQueryGroup>,
    sourceAggregation: Array<HelisaQueryAggregation>
  ): any {
    // tslint:disable-next-line:no-any
    const id: any = {};
    sourceGroup.forEach((groupItem: string | HelisaQueryGroup): void => {
      if (typeof groupItem === 'string') {
        id[groupItem] = '$' + groupItem;
      } else {
        id[groupItem.fieldResponseName] = '$' + groupItem.fieldName;
      }
    });
    // tslint:disable-next-line:no-any
    const group: any = { _id: id };
    this.fields.forEach(
      (
        field:
          | string
          | HelisaConditionalField
          | HelisaFunctionField
          | HelisaSubqueryField
      ): void => {
        if (typeof field === 'string') {
          group[field] = { $first: '$' + field };
        } else {
          group[field.fieldName] = { $first: '$' + field.fieldName };
        }
      }
    );
    sourceAggregation.forEach((aggregation: HelisaQueryAggregation): void => {
      // tslint:disable-next-line:no-any
      const aggregationObj: any = {};
      if (aggregation.type === 'count') {
        aggregationObj.$sum = 1;
        group[aggregation.fieldResponseName] = aggregationObj;
      } else {
        if (
          aggregation.operation !== undefined &&
          aggregation.operation !== null
        ) {
          aggregationObj['$' + aggregation.type] = {};
          aggregationObj['$' + aggregation.type] =
            aggregation.operation.generateOperation();
        } else {
          if (aggregation.fieldName) {
            aggregationObj['$' + aggregation.type] =
              '$' + aggregation.fieldName;
          } else if (aggregation.helisaQueryCondition) {
            aggregationObj['$' + aggregation.type] = generateCondition(
              aggregation.helisaQueryCondition
            );
          }
          if (aggregation.sort !== undefined && aggregation.sort !== null) {
            aggregationObj['$' + aggregation.sort] = generateSorts(
              aggregation.sort
            );
          }
          if (
            aggregation.delimiter !== undefined &&
            aggregation.delimiter !== null
          ) {
            aggregationObj['$' + aggregation.delimiter] = aggregation.delimiter;
          }
          if (
            aggregation.functionField !== undefined &&
            aggregation.functionField !== null
          ) {
            Object.assign(
              aggregationObj,
              generateFunction(aggregation.functionField)
            );
          }
        }
        group[aggregation.fieldResponseName] = aggregationObj;
      }
    });
    return { $group: group };
  }

  // tslint:disable-next-line:no-any
  private generateFilter(): any {
    if (this.filter) {
      return this.filter.generateFilter();
    }
    return null;
  }
}
